import axios from 'axios';
import { app } from './firebaseConfig';
import type { UserCredential } from 'firebase/auth';
import {
  signInWithEmailAndPassword,
  getAuth,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  updateProfile,
  updatePassword,
  verifyBeforeUpdateEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
} from 'firebase/auth';

const auth = getAuth(app);
const baseUrl = import.meta.env.VITE_API_BASE_URL;
const googleProvider = new GoogleAuthProvider();

const loginComEmailESenha = async (email: string, senha: string): Promise<void> => {
  try {
    await signInWithEmailAndPassword(auth, email, senha);
  } catch (error: any) {
    console.log(error);
    throw error;
  }
};


const registrarComEmailESenha = async (
  name: string,
  email: string,
  pwd: string,
): Promise<UserCredential> => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, pwd);
  const user = userCredential.user;

  try {
    await updateProfile(user, { displayName: name });

    const response = await fetch(`${baseUrl}/add-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        uid: user.uid,
        name: name.toLowerCase().trim(),
        email: email.toLowerCase().trim(),
        authProvider: 'local',
      }),
    });

    if (response.ok) {
      // Envia mensagem após novo registro
      fetch(`${baseUrl}/send-message-to-owner`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mensagem: `Novo usuário registrado ao *Nextalk*\n\n*Nome*: ${name}\n*Email*: ${email}`,
        })
      }).catch(error => {
        console.error("Erro ao enviar mensagem:", error);
      });
      console.log('Usuário registrado com sucesso!');
    } else {
      console.error('Erro ao registrar em dbsalepage');
    }

  } catch (error) {
    console.error('Erro ao registrar usuário:', error);
  }
  return userCredential;
}

const alterarSenha = async (novaSenha: string): Promise<void> => {
  try {
    const user = auth.currentUser;

    if (!user) {
      throw new Error("Usuário não autenticado.");
    }

    await updatePassword(user, novaSenha);
  } catch (error) {
    console.error("Erro ao alterar a senha:", error);
    throw error;
  }
};

const alterarEmail = async (
  novoEmail: string,
  senhaAtual: string
): Promise<void> => {
  try {
    const currentUser = auth.currentUser;

    if (!currentUser || !currentUser.email) {
      throw new Error("Usuário não autenticado.");
    }

    const credential = EmailAuthProvider.credential(
      currentUser.email,
      senhaAtual
    );

    await reauthenticateWithCredential(currentUser, credential);
    await verifyBeforeUpdateEmail(currentUser, novoEmail);

  } catch (error: any) {
    if (error.code === 'auth/wrong-password') {
      throw new Error("Senha incorreta. Por favor, verifique e tente novamente.");
    } else if (error.code === 'auth/requires-recent-login') {
      throw new Error("Por favor, faça login novamente antes de alterar o email.");
    } else {
      console.error("Erro ao alterar o email:", error);
      throw error;
    }
  }
};

const esqueciSenha = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    if (error.code === 'auth/user-not-found') {
      console.error('Usuário não encontrado.');
    } else {
      console.error('Erro ao enviar e-mail de redefinição de senha:', error);
    }
    throw error;
  }
};

const logout = () => {
  signOut(auth)
}

const entrarComGoogle = async (): Promise<void> => {
  try {
    const res = await signInWithPopup(auth, googleProvider);
    const user = res.user;
    const name = user.displayName;
    const email = user.email;
    let usuarioExiste = false;

    try {
      const { data } = await axios.get(`${baseUrl}/get-user`, {
        params: { uid: user.uid }
      });
      usuarioExiste = !!data;
    } catch (error: any) {
      if (error.response) {
        if (error.response.status === 404) {
          usuarioExiste = false;
        } else {
          // Outros códigos HTTP indicam problemas reais
          console.error('Erro na comunicação com o servidor:', error.response.status);
          throw error;
        }
      } else if (error.request) {
        // Erro de conexão com o servidor
        console.error('Erro de conexão com o servidor:', error.message);
        throw error;
      } else {
        // Erro na configuração da requisição
        console.error('Erro ao configurar a requisição:', error.message);
        throw error;
      }
    }

    if (usuarioExiste) {
      return;
    }

    const response = await fetch(`${baseUrl}/add-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        uid: user.uid,
        name: name?.toLowerCase().trim(),
        email: email?.toLowerCase().trim(),
        authProvider: 'google',
      }),
    });

    if (response.ok) {
      // Envia mensagem após novo registro
      fetch(`${baseUrl}/send-message-to-owner`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mensagem: `Novo usuário registrado ao *Nextalk*\n\n*Nome*: ${name}\n*Email*: ${email}`,
        })
      }).catch(error => {
        console.error("Erro ao enviar mensagem:", error);
      });
      console.log('Usuário registrado com sucesso!');
    }
  } catch (error) {
    console.error('Erro ao entrar com Google:', error);
  }
}

export {
  auth,
  loginComEmailESenha,
  registrarComEmailESenha,
  alterarSenha,
  alterarEmail,
  logout,
  entrarComGoogle,
  esqueciSenha,
}